
import React from "react";
import SubscribeButton from "./SubscribeButton";
import { useAuth } from "@/context/AuthContext";
import { usePricingPlans } from "@/hooks/usePricingPlans";
import { formatCurrency } from "@/lib/utils";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";

const PricingSection = () => {
  const { user } = useAuth();
  const { plans, loading, error } = usePricingPlans();

  // Features hardcoded para cada plano
  const planFeatures = {
    free: [
      "Analise Automatizada de Imóveis",
      "Resumo de Mercado em Cidades",
      "Acesso completo por 7 dias grátis"
    ],
    basic: [
      "Analise Automatizada de Imóveis",
      "Resumo de Mercado em Cidades",
      "Dashboard de Vendas", 
      "Dashboard de Aluguel",
      "Entraram no Mercado",
      "Saíram do Mercado",
      "Análise das Imobiliárias"
    ]
  };

  if (loading) {
    return (
      <section id="pricing" className="py-20 bg-gradient-to-b from-background to-muted/20">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-64 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-96 mx-auto"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="pricing" className="py-20 bg-gradient-to-b from-background to-muted/20">
        <div className="container mx-auto px-6">
          <div className="text-center">
            <p className="text-red-600">Erro ao carregar planos: {error}</p>
          </div>
        </div>
      </section>
    );
  }

  const freePlan = plans.find(plan => plan.slug === 'free');
  const basicPlan = plans.find(plan => plan.slug === 'basic');

  return (
    <section id="pricing" className="py-20 bg-gradient-to-b from-background to-muted/20">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-foreground mb-4">
            Escolha o Plano Ideal
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Acesso completo aos dados do mercado imobiliário com análises detalhadas
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Plano Gratuito */}
          {freePlan && (
            <div className="bg-card border border-gray-200 rounded-xl p-8 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col">
              <div className="text-center flex-grow">
                <h3 className="text-2xl font-bold text-gray-600 mb-4">{freePlan.name}</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-gray-600">Teste por 7 dias</span>
                </div>
                
                <ul className="text-left space-y-3 mb-8 flex-grow">
                  {planFeatures.free.map((feature, index) => (
                    <li key={index} className="flex items-center">
                      <span className="w-2 h-2 bg-gray-400 rounded-full mr-3"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-auto">
                <Link to="/signup" className="w-full">
                  <Button 
                    variant="outline"
                    className="w-full text-gray-600 border-gray-300 hover:bg-gray-50"
                  >
                    {user ? "Testar por 7 Dias" : "Criar Conta de Teste"}
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* Plano Básico */}
          {basicPlan && (
            <div className="bg-card border-2 border-primary rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 relative flex flex-col">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-medium">
                Mais Popular
              </div>
              <div className="text-center flex-grow">
                <h3 className="text-2xl font-bold text-foreground mb-4">{basicPlan.name}</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-primary">
                    {formatCurrency(basicPlan.price)}
                  </span>
                  <span className="text-muted-foreground">/mês</span>
                </div>
                
                <ul className="text-left space-y-3 mb-8 flex-grow">
                  {planFeatures.basic.map((feature, index) => (
                    <li key={index} className="flex items-center">
                      <span className="w-2 h-2 bg-primary rounded-full mr-3"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-auto">
                <SubscribeButton 
                  planSlug="basic"
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  {user ? "Assinar Plano Básico" : "Começar Agora"}
                </SubscribeButton>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
